import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import * as adminApi from '../../api/admin';
import type { PlatformMfaPolicy } from '../../api/admin';
import { extractErrorMessage } from '../../api/errors';

/**
 * Platform MFA policy (superadmin only).
 *
 * Backed by `public.platform_settings` (key='mfa_policy'). When ON,
 * every active superadmin must enroll TOTP — RequireAuth's gate forces
 * /setup-2fa until they do, and Profile hides the per-user disable
 * button. When OFF, MFA falls back to each superadmin's individual
 * Profile toggle.
 *
 * Sister-surface to TenantSettings → "Require MFA for tenant admins",
 * but at platform scope. Default is ON; saving a flip takes effect on
 * the next /users/me roundtrip (so the next page load for that
 * superadmin, no container restart).
 */
export function PlatformMfaSettings({
  embedded = false,
}: { embedded?: boolean } = {}) {
  const [policy, setPolicy] = useState<PlatformMfaPolicy | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    adminApi
      .getPlatformMfaPolicy()
      .then((p) => setPolicy(p))
      .catch((err: unknown) =>
        setLoadError(extractErrorMessage(err, 'failed to load MFA policy')),
      );
  }, []);

  const onToggle = async (next: boolean) => {
    if (!policy) return;
    setSaving(true);
    setSaveError(null);
    setSavedAt(null);
    try {
      const updated = await adminApi.updatePlatformMfaPolicy({
        totp_required: next,
      });
      setPolicy(updated);
      setSavedAt(Date.now());
    } catch (err: unknown) {
      setSaveError(extractErrorMessage(err, 'failed to update MFA policy'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ maxWidth: 760 }}>
      {!embedded && (
        <Stack>
          <Typography variant="h4">MFA Policy</Typography>
          <Typography variant="body2" color="text.secondary">
            Platform-wide MFA setting for every superadmin. Per-tenant
            enforcement for tenant admins is configured under each
            tenant's Settings page.
          </Typography>
        </Stack>
      )}

      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Superadmin MFA
          </Typography>
          <Divider sx={{ my: 1 }} />

          {loadError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {loadError}
            </Alert>
          )}
          {!policy && !loadError && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={20} />
            </Box>
          )}

          {policy && (
            <>
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Switch
                    checked={policy.totp_required}
                    disabled={saving}
                    onChange={(e) => void onToggle(e.target.checked)}
                  />
                }
                label="Require MFA for platform admins"
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                When ON, every active superadmin must enroll TOTP on
                their next sign-in and cannot disable it from their
                Profile. When OFF, MFA falls back to each admin's
                individual Profile setting.
              </Typography>

              {saveError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {saveError}
                </Alert>
              )}
              {savedAt !== null && !saveError && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Saved. New policy takes effect on the next sign-in.
                </Alert>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
}
